import React, { useEffect, useState } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { ShieldCheck, Certificate, MapPin } from "@phosphor-icons/react";

export default function Credentials() {
  const [data, setData] = useState({ advisor: null, licenses: [] });

  useEffect(() => {
    api.get("/licenses").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const { advisor, licenses } = data;

  return (
    <div data-testid="credentials-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-36 pb-12">
        <p className="eyebrow"><span className="gold-divider" />Accreditation &amp; Licensing</p>
        <h1 className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95]">
          Licensed. Independent. <span className="gold-text italic">Verifiable.</span>
        </h1>
        <p className="text-lg text-exodus-muted mt-8 max-w-2xl leading-relaxed">
          Every advisor should be checkable in the public record. Below is the current license footprint —
          state by state. Verify any of it directly with the issuing department.
        </p>
      </section>

      {advisor && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-16">
          <div data-testid="advisor-profile-card" className="border border-exodus-border bg-exodus-surface p-8 lg:p-12 grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <p className="eyebrow text-exodus-muted">Licensed Advisor</p>
              <h2 className="font-display text-4xl mt-2">{advisor.name}</h2>
              <p className="text-base text-exodus-muted mt-2">{advisor.title}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {advisor.lines_of_authority?.map((l) => (
                  <span
                    key={l}
                    data-testid={`advisor-line-${l.toLowerCase().replace(/\s/g, "-")}`}
                    className="text-xs eyebrow text-exodus-gold border border-exodus-gold/40 px-3 py-1.5"
                  >
                    {l}
                  </span>
                ))}
              </div>
            </div>
            <div className="border-t md:border-t-0 md:border-l border-exodus-border md:pl-8 pt-8 md:pt-0">
              <div className="flex items-start gap-3">
                <Certificate size={22} className="text-exodus-gold mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">{advisor.anti_money_laundering?.course}</p>
                  <p className="text-xs text-exodus-muted mt-1">
                    {advisor.anti_money_laundering?.completed ? "Completed" : "Pending"}
                    {advisor.anti_money_laundering?.issued_year ? ` · ${advisor.anti_money_laundering.issued_year}` : ""}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-32">
        <div className="flex items-center gap-3 mb-10">
          <ShieldCheck size={20} className="text-exodus-gold" />
          <h2 className="font-display text-3xl">Active State Licenses</h2>
          <span data-testid="license-count" className="text-sm text-exodus-muted">{licenses.length} states</span>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-exodus-border">
          {licenses.map((l) => (
            <div
              key={`${l.state}-${l.license_number}`}
              data-testid={`license-card-${l.state.toLowerCase().replace(/\s/g, "-")}`}
              className="bg-exodus-surface p-8 flex flex-col gap-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <MapPin size={18} className="text-exodus-gold" />
                  <h3 className="font-display text-2xl">{l.state}</h3>
                </div>
                {l.resident && (
                  <span className="text-[10px] eyebrow text-exodus-gold border border-exodus-gold/40 px-2 py-1">
                    Resident
                  </span>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <Row label="License #" value={l.license_number} />
                <Row label="Class" value={l.license_class} />
                <Row label="Lines" value={(l.lines_of_authority || []).join(", ")} />
                <Row label="Effective" value={l.effective_date || "—"} />
                <Row label="Expires" value={l.expiration_date || "—"} />
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-exodus-muted mt-10 leading-relaxed max-w-3xl">
          All license numbers can be verified in each state's Department of Insurance public lookup. EXODUS LIFE &amp; WEALTH
          is a brand operated by a licensed independent insurance advisor. All products are issued by third-party A-rated carriers.
        </p>
      </section>

      <Footer />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-exodus-border/40 pb-2">
      <span className="text-xs text-exodus-muted">{label}</span>
      <span className="text-sm text-exodus-text font-mono tracking-tight text-right">{value}</span>
    </div>
  );
}
